import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/**
 * LocationPicker Component
 * 
 * A map-based location picker using React-Leaflet and OpenStreetMap.
 * Allows users to:
 * - Select a location by clicking on the map.
 * - Search for a location using a search bar.
 * - Retrieve the user's current location on component mount.
 * - Display the selected coordinates.
 * 
 * @component
 * @memberof Components
 * @param {Object} props - Component props.
 * @param {function} props.onLocationSelect - Callback function to pass selected coordinates `[latitude, longitude]` to the parent.
 * @param {number[]} [props.location] - Optional initial location `[latitude, longitude]` to set the map marker.
 * 
 * @example
 * const handleLocationSelect = (coords) => {
 *   console.log("Selected location:", coords);
 * };
 * 
 * return <LocationPicker onLocationSelect={handleLocationSelect} location={[31.5204, 74.3587]} />;
 */
const LocationPicker = ({ onLocationSelect, location }) => {
    const [position, setPosition] = useState([31.5204, 74.3587]); // Default: Lahore

    // Get user's location on component mount
    useEffect(() => {
        if (!location) {
            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        setPosition([latitude, longitude]);
                        onLocationSelect([latitude, longitude]); // Set initial selection
                    },
                    (error) => {
                        setPosition([31.5204, 74.3587]); // Default: Lahore
                    }
                );
            } else {
                setPosition([31.5204, 74.3587]); // Default: Lahore
            }
        } else {
            setPosition([location[0], location[1]])
            onLocationSelect([location[0], location[1]])
        }

    }, [onLocationSelect]);

    /**
    * Handles user clicks on the map and updates the selected position.
    * @returns {null} No UI rendered.
    */
    const MapClickHandler = () => {
        useMapEvents({
            click: (e) => {
                const { lat, lng } = e.latlng;
                setPosition([lat, lng]);
                onLocationSelect([lat, lng]); // Pass selected coordinates to parent
            },
        });
        return null;
    };

    /**
     * Adds a search control to the map to allow users to search for locations.
     * Updates the marker position when a search result is selected.
     * @returns {null} No UI rendered.
     */
    const SearchControl = () => {
        const map = useMap(); // Get existing map instance

        useEffect(() => {
            const provider = new OpenStreetMapProvider();
            const searchControl = new GeoSearchControl({
                provider,
                style: "bar",
                showMarker: false, // We will manually update the marker
                autoClose: true, // Close search results automatically
                keepResult: false,
            });

            map.addControl(searchControl); // Attach search control to existing map

            // Listen for search results
            map.on("geosearch/showlocation", (e) => {
                const { x, y } = e.location; // Get coordinates
                setPosition([y, x]); // Update marker position
                onLocationSelect([y, x]);
            });

            return () => {
                map.removeControl(searchControl); // Cleanup on unmount
            };
        }, [map]);

        return null;
    };

    return (
        <div>
            <MapContainer
                center={position}
                zoom={13}
                style={{ height: "400px", width: "100%" }}
                className="leaflet-container"
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution=''
                />
                <Marker position={position} />
                <MapClickHandler />
                <SearchControl />
            </MapContainer>
            <p>Selected Coordinates: {position[0]}, {position[1]}</p>
        </div>
    );
};

export default LocationPicker;
